/**
 * ShareButton — uses Web Share API (mobile / native browsers), falls back to
 * copy-to-clipboard with a "Copied!" toast on desktop / older browsers.
 *
 * Combined with the deep linking from v5.7.10:
 *   - On a phone with the app installed → opens directly inside Fantasy World Star
 *   - Anywhere else → opens the web app at the same URL
 *
 * Usage:
 *   <ShareButton path="/team/abc123" title="My team" text="Check out my fantasy roster" />
 */
import { useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

function _buildShareUrl(path) {
  // Prefer the frontend's actual host (window.location.origin) so links
  // route through the web app's URL handlers (v5.7.14) AND match the AASA
  // / assetlinks domain for Universal Links / App Links.
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  let base = cleanPath;
  if (typeof window !== 'undefined' && window.location?.origin) {
    base = `${window.location.origin}${cleanPath}`;
  } else {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (backend) {
      try {
        const u = new URL(backend);
        base = `${u.protocol}//${u.host}${cleanPath}`;
      } catch { /* fall through */ }
    }
  }
  // v6.x — Tag every shared link so recipients become a measurable
  // acquisition channel in PostHog (signup → draft → roster_complete →
  // ad-free funnel, broken down by Initial UTM Source). Players sharing
  // their team/league are our cheapest, highest-intent growth loop.
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}utm_source=share&utm_medium=referral`;
}

export default function ShareButton({
  path,
  title = 'Fantasy World Star',
  text = 'Check this out on Fantasy World Star',
  imagePath = null,
  variant = 'ghost',
  size = 'sm',
  className = '',
  iconOnly = false,
  testid,
}) {
  const [copied, setCopied] = useState(false);

  const shareImage = async (url) => {
    // Fetch the server-rendered card and hand the FILE to the share sheet
    // (mobile browsers); unsupported → fall back to link sharing below.
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${backend}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`);
    if (!res.ok) throw new Error('card fetch failed');
    const blob = await res.blob();
    const file = new File([blob], 'roster-card.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ title, text, url, files: [file] });
      return true;
    }
    return false;
  };

  const onShare = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const url = _buildShareUrl(path);

    if (imagePath) {
      try {
        if (await shareImage(url)) return;
      } catch (err) {
        if (err && err.name === 'AbortError') return; // user closed the sheet
        // anything else → plain link share below
      }
    }

    // 1. Try the native Web Share API (mobile + modern desktop browsers)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        // User cancelled — silently exit. Anything else falls through to copy.
        if (err && err.name === 'AbortError') return;
      }
    }

    // 2. Fallback — copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied!', { description: url });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 3. Last-resort fallback for very old browsers — prompt the user
      // eslint-disable-next-line no-alert
      window.prompt('Copy this link:', url);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={onShare}
      className={className}
      aria-label="Share"
      data-testid={testid || 'share-button'}
    >
      {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
      {!iconOnly && <span className="ml-2">{copied ? 'Copied' : 'Share'}</span>}
    </Button>
  );
}
